import { useEffect, useState } from 'react';

export interface Location {
  pathname: string;
  search: string;
  hash: string;
}

function readLocation(): Location {
  if (typeof window === 'undefined') return { pathname: '/', search: '', hash: '' };
  const { pathname, search, hash } = window.location;
  return { pathname, search, hash };
}

export function useLocation(): Location {
  const [loc, setLoc] = useState<Location>(readLocation);

  useEffect(() => {
    const sync = () => setLoc(readLocation());
    window.addEventListener('popstate', sync);
    window.addEventListener('sn-route', sync as EventListener);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener('sn-route', sync as EventListener);
    };
  }, []);

  return loc;
}

export function usePathname(): string {
  return useLocation().pathname;
}

export function useSearchParams(): URLSearchParams {
  return new URLSearchParams(useLocation().search);
}

export interface NavigateOptions {
  search?: string | URLSearchParams | Record<string, string | undefined> | null;
  hash?: string | null;
  replace?: boolean;
}

function serializeSearch(input: NavigateOptions['search']): string {
  if (input == null) return '';
  if (typeof input === 'string') {
    if (!input) return '';
    return input.startsWith('?') ? input : `?${input}`;
  }
  if (input instanceof URLSearchParams) {
    const s = input.toString();
    return s ? `?${s}` : '';
  }
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(input)) {
    if (v != null && v !== '') usp.set(k, v);
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

/**
 * Navigate to a new URL. Accepts a path that may already include `?search` and `#hash`,
 * and an options bag to override either part explicitly.
 *
 * - `navigate('/foo')` → /foo (search + hash cleared)
 * - `navigate('/foo?a=1#3')` → /foo?a=1#3
 * - `navigate('/foo', { search: 'a=1', hash: '#3' })` → /foo?a=1#3
 * - opts.search / opts.hash override whatever the path encoded.
 */
export function navigate(input: string, opts: NavigateOptions = {}): void {
  if (typeof window === 'undefined') return;
  const parsed = new URL(input, window.location.href);
  const pathname = parsed.pathname;
  const search = opts.search !== undefined ? serializeSearch(opts.search) : parsed.search;
  const hash = opts.hash !== undefined ? opts.hash ?? '' : parsed.hash;
  const url = `${pathname}${search}${hash}`;
  if (opts.replace) window.history.replaceState(null, '', url);
  else window.history.pushState(null, '', url);
  window.dispatchEvent(new Event('sn-route'));
}
